const highlightsAuraMotionSelector = '[data-highlights-aura-motion]';
const highlightsAuraMotionInstances = new WeakMap();
const observedHighlightsAuraControllers = new WeakSet();
const visibleHighlightsAuraControllers = new Set();
let isVisibilityListenerAttached = false;

const getReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const canRunHighlightsAuraMotion = ({ width, reducedMotion, gsap }) =>
  width > 1024 &&
  !reducedMotion &&
  Boolean(
    gsap &&
      typeof gsap.set === 'function' &&
      typeof gsap.timeline === 'function'
  );

export const createHighlightsAuraMotionController = (
  target,
  {
    width = typeof window === 'undefined' ? 0 : window.innerWidth,
    reducedMotion = getReducedMotion(),
    gsap = globalThis.gsap,
  } = {}
) => {
  if (
    !target ||
    !canRunHighlightsAuraMotion({ width, reducedMotion, gsap })
  ) {
    return null;
  }

  gsap.set(target, {
    xPercent: 0,
    yPercent: 0,
    rotation: 0,
    transformOrigin: 'center center',
  });

  const timeline = gsap
    .timeline({ repeat: -1 })
    .to(target, {
      xPercent: 4,
      yPercent: -2.5,
      rotation: 10,
      duration: 18,
      ease: 'sine.inOut',
    })
    .to(target, {
      xPercent: -3.5,
      yPercent: 3,
      rotation: -12,
      duration: 18,
      ease: 'sine.inOut',
    })
    .to(target, {
      xPercent: 2.5,
      yPercent: 1,
      rotation: 7,
      duration: 18,
      ease: 'sine.inOut',
    })
    .to(target, {
      xPercent: 0,
      yPercent: 0,
      rotation: 0,
      duration: 18,
      ease: 'sine.inOut',
    });

  let isInViewport = true;
  let isDocumentVisible =
    typeof document === 'undefined' || document.visibilityState !== 'hidden';

  const syncPlayback = () => {
    const method = isInViewport && isDocumentVisible ? 'play' : 'pause';
    timeline[method]();
  };

  const controller = {
    pause() {
      isInViewport = false;
      syncPlayback();
    },
    resume() {
      isInViewport = true;
      syncPlayback();
    },
    setDocumentVisible(isVisible) {
      isDocumentVisible = isVisible;
      syncPlayback();
    },
    destroy() {
      visibleHighlightsAuraControllers.delete(controller);
      timeline.kill();
    },
  };

  syncPlayback();
  return controller;
};

const observeHighlightsAuraController = (target, controller) => {
  if (typeof IntersectionObserver === 'undefined') return;

  controller.pause();
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.target !== target) return;
      if (entry.isIntersecting) controller.resume();
      else controller.pause();
    });
  });
  observer.observe(target);
};

const listenForDocumentVisibility = (controller) => {
  if (typeof document === 'undefined') return;

  visibleHighlightsAuraControllers.add(controller);
  if (isVisibilityListenerAttached) return;

  document.addEventListener('visibilitychange', () => {
    const isVisible = document.visibilityState !== 'hidden';
    visibleHighlightsAuraControllers.forEach((activeController) => {
      activeController.setDocumentVisible(isVisible);
    });
  });
  isVisibilityListenerAttached = true;
};

export const initHighlightsAuraMotion = (
  scope = document,
  {
    observe = observeHighlightsAuraController,
    listenForVisibility = listenForDocumentVisibility,
    ...controllerOptions
  } = {}
) => {
  if (!scope || typeof scope.querySelectorAll !== 'function') return [];

  return [...scope.querySelectorAll(highlightsAuraMotionSelector)]
    .map((target) => {
      if (!highlightsAuraMotionInstances.has(target)) {
        highlightsAuraMotionInstances.set(
          target,
          createHighlightsAuraMotionController(target, controllerOptions)
        );
      }

      const controller = highlightsAuraMotionInstances.get(target);
      if (!controller || observedHighlightsAuraControllers.has(controller)) {
        return controller;
      }

      observedHighlightsAuraControllers.add(controller);
      observe(target, controller);
      listenForVisibility(controller);
      return controller;
    })
    .filter(Boolean);
};

if (typeof document !== 'undefined') {
  initHighlightsAuraMotion();
}
